import { useCallback, useEffect, useRef, useState } from 'react';

import { AnimationT } from '@components/suggestions/animations/quickActionAnimation';
import { useSuggestionsContext } from '@components/suggestions/context/useSuggestionsContext';
import { useAnimation } from 'framer-motion';

import { EVENTS } from '@/analytics/consts';
import { useAnalytics } from '@/analytics/useAnalytics';
import {
  QuickActionDto,
  QuickActionDtoStatus,
  QuickActionDtoType,
  getEmailThread
} from '@/api/generated';
import useQuickActions from '@/hooks/useQuickActions';
import { launchErrorToast } from '@/utils/toast';

const NUM_QUICK_ACTIONS = 3;

export const useSuggestionsLogic = () => {
  const { captureEvent } = useAnalytics();
  const { actionFunction, disabled } = useSuggestionsContext();
  const { snoozeQuickAction, patchQuickAction, refetchNextQuickAction } = useQuickActions({
    count: NUM_QUICK_ACTIONS
  });
  const firstQuickActionControls = useAnimation();
  const secondQuickActionControls = useAnimation();

  const [isLoadingQuickActions, setIsLoadingQuickActions] = useState(true);
  const [localQuickActions, setLocalQuickActions] = useState<QuickActionDto[]>([]);

  const isAnimatingRef = useRef(false);

  const handleAnimationError = useCallback(
    (error: Error) => {
      firstQuickActionControls.start(AnimationT.Initial);
      secondQuickActionControls.start(AnimationT.Initial);
      launchErrorToast();
    },
    [firstQuickActionControls, secondQuickActionControls]
  );

  useEffect(() => {
    const fetchNextQuickActions = async () => {
      try {
        const res = await refetchNextQuickAction();
        setLocalQuickActions(res.data?.items || []);
      } catch (error) {
        launchErrorToast();
      } finally {
        setIsLoadingQuickActions(false);
      }
    };
    fetchNextQuickActions();
  }, [refetchNextQuickAction]);

  // TODO: This is a temporary solution to fetch email threads for task follow-ups and email replies.
  // We need to find a better way to do this.
  useEffect(() => {
    const fetchEmailThreads = async () => {
      const quickActionsToFetch = localQuickActions.filter(
        (quickAction: QuickActionDto) =>
          quickAction.sourceId &&
          (quickAction.type === QuickActionDtoType.taskFollowUp ||
            quickAction.type === QuickActionDtoType.emailReply)
      );

      const results = await Promise.allSettled(
        quickActionsToFetch.map(async (quickAction: QuickActionDto) => {
          const res = await getEmailThread(quickAction.sourceId!);
          return {
            ...quickAction,
            emailThread: res.items?.[0]
          };
        })
      );

      // Collect failed sourceIds
      const failedQuickActions = quickActionsToFetch
        .filter((_, idx) => results[idx].status === 'rejected')
        .map(quickAction => quickAction);

      if (failedQuickActions.length > 0) {
        await Promise.all(
          failedQuickActions.map(async (quickAction: QuickActionDto) => {
            await patchQuickAction(quickAction.id!, {
              ...quickAction,
              status: QuickActionDtoStatus.done
            });
          })
        );
        const res = await refetchNextQuickAction();
        setLocalQuickActions(res.data?.items || []);
      }
    };

    fetchEmailThreads();
  }, [localQuickActions]);

  //////////////////////////////////////////////////////////////

  const handleDismiss = useCallback(async () => {
    if (isAnimatingRef.current) return;

    const quickAction = localQuickActions[0];
    if (!quickAction.id) return;
    captureEvent(EVENTS.QUICK_ACTION_CLICKED_DISMISSED, {
      quickActionId: quickAction.id,
      sourceId: quickAction.sourceId,
      type: quickAction.type
    });
    isAnimatingRef.current = true;
    try {
      await Promise.all([
        patchQuickAction(quickAction.id, {
          ...quickAction,
          status: QuickActionDtoStatus.dismissed
        }),
        firstQuickActionControls.start(AnimationT.Left),
        secondQuickActionControls.start(AnimationT.Straight)
      ]);

      const res = await refetchNextQuickAction();
      setLocalQuickActions(res.data?.items || []);
    } catch (error) {
      handleAnimationError(error as Error);
    } finally {
      isAnimatingRef.current = false;
    }
  }, [
    localQuickActions,
    firstQuickActionControls,
    secondQuickActionControls,
    patchQuickAction,
    refetchNextQuickAction,
    handleAnimationError
  ]);

  const handleApprove = useCallback(async () => {
    if (isAnimatingRef.current || disabled) return;

    const quickAction = localQuickActions[0];
    if (!quickAction.id) return;

    isAnimatingRef.current = true;
    captureEvent(EVENTS.QUICK_ACTION_CLICKED_APPROVED, {
      quickActionId: quickAction.id,
      sourceId: quickAction.sourceId,
      type: quickAction.type
    });
    try {
      await Promise.all([
        actionFunction(),
        firstQuickActionControls.start(AnimationT.Right),
        secondQuickActionControls.start(AnimationT.Straight)
      ]);
      await patchQuickAction(quickAction.id, {
        ...quickAction,
        status: QuickActionDtoStatus.done
      });
      const res = await refetchNextQuickAction();
      setLocalQuickActions(res.data?.items || []);
    } catch (error) {
      handleAnimationError(error as Error);
    } finally {
      isAnimatingRef.current = false;
    }
  }, [
    localQuickActions,
    firstQuickActionControls,
    secondQuickActionControls,
    patchQuickAction,
    handleAnimationError,
    refetchNextQuickAction,
    actionFunction,
    disabled
  ]);

  const handleSnooze = useCallback(async () => {
    if (isAnimatingRef.current) return;

    const quickAction = localQuickActions[0];
    if (!quickAction.id) return;

    isAnimatingRef.current = true;

    try {
      await Promise.all([
        snoozeQuickAction(quickAction.id),
        firstQuickActionControls.start(AnimationT.Top),
        secondQuickActionControls.start(AnimationT.Straight)
      ]);

      const res = await refetchNextQuickAction();
      setLocalQuickActions(res.data?.items || []);
    } catch (error) {
      handleAnimationError(error as Error);
    } finally {
      isAnimatingRef.current = false;
    }
  }, [
    localQuickActions,
    firstQuickActionControls,
    secondQuickActionControls,
    handleAnimationError,
    snoozeQuickAction,
    refetchNextQuickAction
  ]);

  const quickActionsToShow = [...localQuickActions.slice(0, 2)];

  return {
    disabled,
    isLoadingQuickActions,
    quickActionsToShow,
    firstQuickActionControls,
    secondQuickActionControls,
    handleDismiss,
    handleSnooze,
    handleApprove
  };
};
